import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { cardHover, ease, modalBackdrop, modalContent } from '../../motion/variants';

const SingleProject = ({
  coverimg,
  name,
  badge,
  img1,
  description,
  liveDemo,
  github,
  category,
  client,
  duration,
  teamSize,
  technologies,
  challenges,
  solutions,
  results,
  featured,
  status,
}) => {
  const [showModal, setShowModal] = useState(false);

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      setShowModal(false);
    }
  };

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        setShowModal(false);
      }
    };

    if (showModal) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [showModal]);

  return (
    <>
      <motion.article
        initial="rest"
        whileHover="hover"
        variants={cardHover}
        transition={{ duration: 0.35, ease }}
        className={`overflow-hidden h-auto rounded-2xl border border-white/[0.08] bg-white/[0.02] hover:border-white/[0.14] ${
          featured ? 'ring-2 ring-ant-gold/30' : ''
        }`}
      >
        <div className="project-frame relative mx-3 sm:mx-4 mt-3 sm:mt-4">
          <img
            alt={name}
            src={coverimg}
            loading="lazy"
            className="h-44 sm:h-52 w-full object-cover object-top"
          />
          <div className="absolute top-3 left-3 sm:top-4 sm:left-4">
            <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-ant-gold/10 text-ant-gold border border-ant-gold/20">
              {category}
            </span>
          </div>
        </div>

        <div className="p-4 sm:p-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-3">
            <h3 className="text-lg sm:text-xl font-bold text-white">{name}</h3>
            {badge}
          </div>

          <div className="grid grid-cols-1 xs:grid-cols-2 gap-2 sm:gap-4 mb-4 text-xs sm:text-sm text-white/45">
            <div>
              <span className="font-medium text-white/55">Client:</span> {client}
            </div>
            <div>
              <span className="font-medium text-white/55">Duration:</span> {duration}
            </div>
            <div>
              <span className="font-medium text-white/55">Team:</span> {teamSize}
            </div>
            <div>
              <span className="font-medium text-white/55">Status:</span> {status}
            </div>
          </div>

          <div className="mb-4">
            <h4 className="text-sm font-medium text-white/60 mb-2">Stack</h4>
            <div className="flex flex-wrap gap-2">
              {technologies?.map((tech) => (
                <span
                  key={tech}
                  className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-white/[0.06] text-white/60 hover:bg-ant-green/10 hover:text-ant-green transition-colors duration-200"
                >
                  {tech}
                </span>
              ))}
            </div>
          </div>

          <p className="text-white/45 text-sm leading-relaxed mb-4 line-clamp-3">{description}</p>

          <motion.button
            whileTap={{ scale: 0.97 }}
            className="w-full bg-white text-ant-bg-2 font-semibold text-sm px-4 py-2.5 rounded-full outline-none focus:outline-none hover:bg-white/90 transition-all duration-200"
            onClick={() => setShowModal(true)}
            aria-label={`View details for ${name} project`}
          >
            View case study
          </motion.button>
        </div>
      </motion.article>

      <AnimatePresence>
        {showModal && (
          <motion.div
            variants={modalBackdrop}
            initial="hidden"
            animate="visible"
            exit="exit"
            className="justify-center items-end sm:items-center flex overflow-x-hidden overflow-y-auto fixed inset-0 z-[60] p-0 sm:p-4 outline-none focus:outline-none bg-black/70"
            onClick={handleBackdropClick}
            role="dialog"
            aria-modal="true"
            aria-labelledby="modal-title"
          >
            <motion.div
              variants={modalContent}
              initial="hidden"
              animate="visible"
              exit="exit"
              className="relative w-full sm:max-w-4xl sm:my-6 mx-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="border border-white/[0.08] rounded-t-2xl sm:rounded-2xl shadow-2xl relative flex flex-col w-full max-h-[92vh] sm:max-h-[85vh] bg-ant-bg-2 outline-none focus:outline-none">
                <div className="flex items-start justify-between p-4 sm:p-6 border-b border-white/[0.06] rounded-t-2xl shrink-0">
                  <div className="flex items-center space-x-3">
                    <h3 id="modal-title" className="text-lg sm:text-2xl font-bold text-white pr-2">
                      {name}
                    </h3>
                    {badge}
                  </div>
                  <motion.button
                    whileTap={{ scale: 0.9 }}
                    className="p-2 ml-auto bg-transparent border-0 text-white/40 text-3xl leading-none font-semibold outline-none focus:outline-none hover:text-white/70 transition-colors"
                    onClick={() => setShowModal(false)}
                    aria-label="Close modal"
                  >
                    <span className="text-2xl">×</span>
                  </motion.button>
                </div>

                <div className="relative p-4 sm:p-6 overflow-y-auto flex-1">
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8">
                    <div>
                      <div className="project-frame">
                        <img alt={name} src={img1} loading="lazy" className="w-full object-cover" />
                      </div>

                      <div className="mt-4 p-4 bg-white/[0.03] rounded-xl border border-white/[0.06]">
                        <h4 className="font-semibold text-white mb-3">Project details</h4>
                        <div className="space-y-2 text-sm">
                          <div className="flex justify-between">
                            <span className="text-white/40">Category</span>
                            <span className="font-medium">{category}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-white/40">Client</span>
                            <span className="font-medium">{client}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-white/40">Duration</span>
                            <span className="font-medium">{duration}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-white/40">Team size</span>
                            <span className="font-medium">{teamSize}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-white/40">Status</span>
                            <span className="font-medium">{status}</span>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="space-y-6">
                      <div>
                        <h4 className="text-lg font-semibold text-white mb-2">Overview</h4>
                        <p className="text-white/50 leading-relaxed">{description}</p>
                      </div>

                      <div>
                        <h4 className="text-lg font-semibold text-white mb-2">Technologies</h4>
                        <div className="flex flex-wrap gap-2">
                          {technologies?.map((tech) => (
                            <span
                              key={tech}
                              className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-ant-green/10 text-ant-green border border-ant-green/20"
                            >
                              {tech}
                            </span>
                          ))}
                        </div>
                      </div>

                      <div>
                        <h4 className="text-lg font-semibold text-white mb-2">Challenges</h4>
                        <p className="text-white/50 leading-relaxed">{challenges}</p>
                      </div>

                      <div>
                        <h4 className="text-lg font-semibold text-white mb-2">Approach</h4>
                        <p className="text-white/50 leading-relaxed">{solutions}</p>
                      </div>

                      <div>
                        <h4 className="text-lg font-semibold text-white mb-2">Outcomes</h4>
                        <p className="text-white/50 leading-relaxed">{results}</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 p-4 sm:p-6 border-t border-white/[0.06] rounded-b-2xl shrink-0">
                  <div className="flex flex-col sm:flex-row gap-2 sm:space-x-3">
                    {liveDemo && (
                      <motion.a
                        whileTap={{ scale: 0.97 }}
                        href={liveDemo}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center justify-center px-4 py-2 text-sm font-medium text-ant-bg-2 bg-white rounded-full hover:bg-white/90 transition-colors duration-200"
                      >
                        <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                        </svg>
                        Live site
                      </motion.a>
                    )}

                    {github && (
                      <motion.a
                        whileTap={{ scale: 0.97 }}
                        href={github}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center justify-center px-4 py-2 text-sm font-medium text-white/70 border border-white/15 rounded-full hover:text-white hover:border-white/30 transition-colors duration-200"
                      >
                        <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 24 24">
                          <path fillRule="evenodd" d="M12 2C6.48 2 2 6.48 2 12c0 4.42 2.865 8.167 6.84 9.49.5.094.68-.217.68-.48 0-.237-.008-.866-.013-1.7-2.782.604-3.365-1.34-3.365-1.34-.454-1.155-1.11-1.464-1.11-1.464-.908-.62.07-.608.07-.608 1.004.07 1.53 1.03 1.53 1.03.89 1.525 2.34 1.085 2.91.83.093-.645.35-1.086.635-1.336-2.22-.25-4.555-1.11-4.555-4.937 0-1.09.39-1.98 1.03-2.68-.103-.25-.448-1.27.097-2.647 0 0 .84-.27 2.75 1.024A9.57 9.57 0 0112 6.763c.855.004 1.715.116 2.517.345 1.91-1.295 2.75-1.024 2.75-1.024.546 1.377.2 2.397.1 2.647.64.7 1.03 1.59 1.03 2.68 0 3.837-2.34 4.687-4.57 4.937.36.31.68.918.68 1.848 0 1.335-.012 2.41-.012 2.735 0 .265.18.575.688.48C19.135 20.167 22 16.42 22 12c0-5.52-4.48-10-10-10z" clipRule="evenodd" />
                        </svg>
                        Source code
                      </motion.a>
                    )}
                  </div>

                  <button
                    className="text-white/40 background-transparent font-medium px-6 py-2 text-sm outline-none focus:outline-none hover:text-white/70 transition-colors duration-200"
                    onClick={() => setShowModal(false)}
                  >
                    Close
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default SingleProject;